import React, { useMemo, useRef, useState } from 'react';
import { Callout, DirectionalHint } from '@fluentui/react';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { CancelIcon } from '@fluentui/react-icons-mdl2';

import AddressBookIcon from '../img/icon-AddressBook-white.png';
import AddressBookHoverIcon from '../img/icon-AddressBook-hover-over-state.png';
import PlusIcon from '../img/icon-plus-white.png';
import PlusHoverIcon from '../img/icon-plus-hover-over-state.png';
import DeleteIcon from '../img/icon-Delete2-white.png';
import DeleteHoverIcon from '../img/icon-Delete2-mouse-over.png';
import UnreadIcon from '../img/icon-Unread-White.png';
import UnreadHoverIcon from '../img/icon-Unread-White-MouseWhite.png';
import SetTopIcon from '../img/icon- ToTop-White.png';
import SetTopHoverIcon from '../img/icon-ToTop-White- MouseOver.png';
import SetDownIcon from '../img/icon- NotToTop-White.png';
import SetDownHoverIcon from '../img/icon- NotToTop-White- MouseOver.png';
import { useIdigestChatTheme } from '../ThemeContext';
import { chatThemeCssVars } from '../theme';
const circularButtonStyles = {
  root: {
    width: '30px',
    height: '30px',
    minWidth: '30px',
    minHeight: '30px',
    padding: 0,
    margin: 0,
    borderRadius: '50%',
    backgroundColor: 'transparent',
    fontSize: '16px',
    fontWeight: '600',
    position: 'relative' as const,
    selectors: {
      '&.ms-Button': {
        borderRadius: '50%'
      }
    }
  },
  rootHovered: {
    backgroundColor: 'transparent'
  },
  rootPressed: {
    backgroundColor: 'transparent'
  },
  flexContainer: {
    justifyContent: 'center' as const,
    alignItems: 'center' as const
  }
};

export interface IGroupListHeaderProps {
  onNewGroup: () => void;
  onMassMessage: () => void;
  onShowContacts: () => void;
  t: (key: string) => string;
  selectedCount?: number;
  selectionMode?: boolean;
  onClearSelection?: () => void;
  onDelete?: () => void;
  onMarkUnread?: () => void;
  /** When true, selection action unpins; otherwise pins. */
  selectionAreTop?: boolean;
  onToggleTop?: () => void;
}

export const GroupListHeader: React.FC<IGroupListHeaderProps> = ({
  onNewGroup,
  onMassMessage,
  onShowContacts,
  t,
  selectedCount = 0,
  selectionMode = false,
  onClearSelection,
  onDelete,
  onMarkUnread,
  selectionAreTop = false,
  onToggleTop
}) => {
  const [isContactHovered, setIsContactHovered] = useState(false);
  const [isAddHovered, setIsAddHovered] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [isUnreadHovered, setIsUnreadHovered] = useState(false);
  const [isTopHovered, setIsTopHovered] = useState(false);
  const [isCreatePopoverOpen, setIsCreatePopoverOpen] = useState(false);
  const createButtonRef = useRef<HTMLDivElement>(null);
  const { theme, tokens } = useIdigestChatTheme();
  const topActionLabel = t(selectionAreTop ? 'GroupScreen.SetDownGroup' : 'GroupScreen.SetTopGroup');
  const topActionIcon = selectionAreTop
    ? isTopHovered
      ? SetDownHoverIcon
      : SetDownIcon
    : isTopHovered
      ? SetTopHoverIcon
      : SetTopIcon;

  const createMenuButtonStyles = useMemo(
    () => ({
      root: {
        backgroundColor: tokens.dialogButtonBg,
        color: tokens.dialogButtonFg,
        padding: '8px',
        border: `1px solid ${tokens.border}`,
        borderRadius: '20px',
        textAlign: 'center' as const,
        minWidth: 'auto',
        width: '100%'
      },
      rootHovered: {
        backgroundColor: tokens.surfaceHover,
        color: tokens.fg,
        border: `1px solid ${tokens.border}`
      },
      rootPressed: {
        backgroundColor: tokens.surfaceHover,
        color: tokens.fg,
        border: `1px solid ${tokens.border}`
      },
      label: {
        color: 'inherit',
        fontWeight: '600' as const
      }
    }),
    [tokens]
  );

  const createPopoverSurfaceStyle = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '6px',
      padding: 0,
      minWidth: '140px',
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
      ...chatThemeCssVars(tokens)
    }),
    [tokens]
  );

  const handleCreateMenuAction = (action: () => void) => {
    setIsCreatePopoverOpen(false);
    action();
  };

  if (selectionMode || selectedCount > 0) {
    return (
      <div className='panel-header idigest-chat-header delete-mode-header'>
        <div className='delete-mode-cancel-container'>
          {onClearSelection && (
            <DefaultButton
              title={t('Cancel')}
              ariaLabel={t('Cancel')}
              className='delete-mode-cancel-button'
              styles={{
                root: {
                  backgroundColor: 'transparent',
                  border: 'none',
                  minWidth: 'auto',
                  width: '28px',
                  height: '28px',
                  padding: 0
                },
                rootHovered: {
                  backgroundColor: 'transparent'
                }
              }}
              onClick={onClearSelection}
            >
              <CancelIcon className='delete-mode-cancel-icon' />
            </DefaultButton>
          )}
          <span className='delete-mode-selected-count'>
            {t('GroupScreen.SelectedCount').replace('{{count}}', selectedCount.toString())}
          </span>
        </div>
        <div className='header-actions'>
          {selectedCount > 0 && onToggleTop && (
            <div className='button-with-label'>
              <DefaultButton
                className='circular-button top-action-button'
                title={topActionLabel}
                ariaLabel={topActionLabel}
                styles={{
                  ...circularButtonStyles,
                  root: {
                    ...circularButtonStyles.root,
                    backgroundColor: 'transparent'
                  },
                  rootHovered: {
                    ...circularButtonStyles.rootHovered
                  }
                }}
                onClick={onToggleTop}
                disabled={selectedCount === 0}
                onMouseEnter={() => setIsTopHovered(true)}
                onMouseLeave={() => setIsTopHovered(false)}
              >
                <img
                  src={topActionIcon}
                  alt={topActionLabel}
                  className='normal top-mark-icon'
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </DefaultButton>
              <span className='delete-button-label'>{topActionLabel}</span>
            </div>
          )}
          {selectedCount > 0 && onMarkUnread && (
            <div className='button-with-label'>
              <DefaultButton
                className='circular-button'
                title={t('GroupScreen.PendingRead')}
                ariaLabel={t('GroupScreen.PendingRead')}
                styles={{
                  ...circularButtonStyles,
                  root: {
                    ...circularButtonStyles.root,
                    backgroundColor: 'transparent'
                  },
                  rootHovered: {
                    ...circularButtonStyles.rootHovered
                  }
                }}
                onClick={onMarkUnread}
                disabled={selectedCount === 0}
                onMouseEnter={() => setIsUnreadHovered(true)}
                onMouseLeave={() => setIsUnreadHovered(false)}
              >
                <img
                  src={isUnreadHovered ? UnreadHoverIcon : UnreadIcon}
                  alt={t('GroupScreen.PendingRead')}
                  className='normal'
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </DefaultButton>
              <span className='delete-button-label'>{t('GroupScreen.PendingRead')}</span>
            </div>
          )}
          {selectedCount > 0 && onDelete && (
            <div className='button-with-label'>
              <DefaultButton
                className='circular-button'
                title={t('Delete')}
                ariaLabel={t('Delete')}
                styles={{
                  ...circularButtonStyles,
                  root: {
                    ...circularButtonStyles.root,
                    backgroundColor: '#d32f2f'
                  },
                  rootHovered: {
                    backgroundColor: '#b71c1c'
                  }
                }}
                onClick={onDelete}
                disabled={selectedCount === 0}
                onMouseEnter={() => setIsDeleteHovered(true)}
                onMouseLeave={() => setIsDeleteHovered(false)}
              >
                <img
                  src={isDeleteHovered && selectedCount > 0 ? DeleteHoverIcon : DeleteIcon}
                  alt='Delete'
                  className='normal'
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </DefaultButton>
              <span className='delete-button-label'>{t('authoring.Delete')}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='panel-header idigest-chat-header'>
      <div className='header-title'>{t('GroupList')}</div>
      <div className='header-actions'>
        <div className='button-with-label'>
          <DefaultButton
            className='circular-button'
            title={t('Contact')}
            ariaLabel={t('Contact')}
            styles={circularButtonStyles}
            onClick={onShowContacts}
            onMouseEnter={() => setIsContactHovered(true)}
            onMouseLeave={() => setIsContactHovered(false)}
          >
            <img
              src={isContactHovered ? AddressBookHoverIcon : AddressBookIcon}
              alt='Contact'
              className='normal group-list-header-icon'
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </DefaultButton>
          <span style={{ opacity: 1 }}>{t('Contact')}</span>
        </div>

        <div className='button-with-label' ref={createButtonRef}>
          <DefaultButton
            className='circular-button'
            title={t('Create')}
            ariaLabel={t('Create')}
            styles={circularButtonStyles}
            onClick={() => setIsCreatePopoverOpen((open) => !open)}
            onMouseEnter={() => setIsAddHovered(true)}
            onMouseLeave={() => setIsAddHovered(false)}
          >
            <img
              src={isAddHovered ? PlusHoverIcon : PlusIcon}
              alt='Add'
              className='normal group-list-header-icon'
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </DefaultButton>
          <span style={{ opacity: 1 }}>{t('Create')}</span>
        </div>
        {isCreatePopoverOpen && (
          <Callout
            target={createButtonRef}
            onDismiss={() => setIsCreatePopoverOpen(false)}
            directionalHint={DirectionalHint.topCenter}
            gapSpace={-12}
            isBeakVisible={false}
            className={`group-list-create-popover idigest-chat ${
              theme === 'dark' ? 'idigest-chat--dark' : 'idigest-chat--light'
            }`}
            styles={{
              root: { zIndex: 100001 },
              calloutMain: {
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                padding: 0,
                overflow: 'visible'
              }
            }}
          >
            <div style={createPopoverSurfaceStyle}>
              <DefaultButton styles={createMenuButtonStyles} onClick={() => handleCreateMenuAction(onNewGroup)}>
                {t('AddNewGroup')}
              </DefaultButton>
              <DefaultButton styles={createMenuButtonStyles} onClick={() => handleCreateMenuAction(onMassMessage)}>
                {t('MassMessage.Button')}
              </DefaultButton>
            </div>
          </Callout>
        )}
      </div>
    </div>
  );
};
